import { Component, Input } from '@angular/core';
import { PosSoundService } from '../pos-sound.service';

/**
 * Always-visible sound control.
 * The speaker icon reflects the current state (off / low / high); tapping it
 * opens a compact popover with a volume slider and a mute toggle — no modal,
 * one tap to reach.
 *
 * Used both in the mobile top bar (icon only) and in the desktop sidebar,
 * where `withLabel` renders an icon + "Töne" caption to match the other
 * sidebar buttons.
 */
@Component({
    selector: 'app-sound-control',
    template: `
    <button type="button"
            class="btn text-muted text-hover-primary btn-focus-no-shadow"
            [class.py-1]="!withLabel"
            [ngbPopover]="soundPopover" popoverClass="sound-popover"
            autoClose="outside" [placement]="withLabel ? 'right' : 'bottom-start'"
            container="body" title="Töne" i18n-title>
      <span *ngIf="!withLabel" class="material-icons">{{ iconName }}</span>
      <span *ngIf="withLabel" class="d-flex w-100 flex-column align-items-center">
        <span class="material-icons md-24">{{ iconName }}</span>
        <span class="icon-text" i18n>Töne</span>
      </span>
    </button>

    <ng-template #soundPopover>
      <div class="sound-popover-body d-flex flex-column">
        <div class="d-flex align-items-center gap-2 sound-popover-header">
          <span class="material-icons sound-icon-sm text-info">{{ iconName }}</span>
          <span class="fw-semibold" i18n>Töne</span>
        </div>

        <div class="d-flex align-items-center justify-content-between mb-1">
          <span class="small text-muted" i18n>Lautstärke</span>
          <span class="small fw-semibold" [class.text-muted]="sound.isMuted()">{{ volumePercent }}%</span>
        </div>

        <div class="d-flex align-items-center gap-2 mb-3">
          <span class="material-icons sound-icon-sm text-muted">volume_down</span>
          <input type="range" class="form-range flex-grow-1" min="0" max="100" step="1"
                 [value]="volumePercent" [disabled]="sound.isMuted()"
                 (input)="onVolumeInput($event)" (change)="previewVolume()" />
          <span class="material-icons sound-icon-sm text-muted">volume_up</span>
        </div>

        <button type="button"
                class="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
                [class.btn-danger]="sound.isMuted()"
                [class.btn-outline-secondary]="!sound.isMuted()"
                (click)="toggleMute()">
          <span class="material-icons sound-icon-sm">{{ sound.isMuted() ? 'volume_off' : 'volume_up' }}</span>
          <span *ngIf="!sound.isMuted()" i18n>Stumm schalten</span>
          <span *ngIf="sound.isMuted()" i18n>Ton aktivieren</span>
        </button>
      </div>
    </ng-template>
  `,
    styles: [`
    @use 'variables' as *;

    :host {
      display: block;
    }

    /* Sidebar mode (withLabel): hide the caption until desktop height, exactly
       like app-sidebar-button, so the icon+label line up with the other items. */
    .icon-text {
      display: none;
    }

    @media (min-height: $screen-height-desktop) {
      .icon-text {
        display: block;
      }
    }

    .sound-popover-body {
      min-width: 240px;
    }

    /* Title bar mirrors the app's .modal-header: plain, just a bottom border.
       Negative margins bleed it to the card edges (cancel the body padding). */
    .sound-popover-header {
      margin: -16px -18px 14px;
      padding: 10px 16px;
      border-bottom: 1px solid var(--bs-border-color);
    }

    .sound-icon-sm {
      font-size: 18px;
    }

    /* The popover renders at <body> (container="body"), so style it globally.
       Follows the app conventions: 6px radius, --bs-border-color, a shadow on
       par with the app's .shadow so it lifts off the white page. */
    ::ng-deep .popover.sound-popover {
      --bs-popover-border-color: var(--bs-border-color);
      --bs-popover-border-radius: 6px;
      --bs-popover-max-width: 280px;
      box-shadow: 0 0.5rem 1.25rem rgba(0, 0, 0, 0.18);
    }

    ::ng-deep .popover.sound-popover .popover-body {
      padding: 16px 18px;
    }

    /* Slider accent in the app's blue (default form-range thumb is the brand
       red --bs-primary); the POS runs in a WebKit/Blink webview. */
    ::ng-deep .popover.sound-popover .form-range::-webkit-slider-thumb {
      background-color: var(--bs-info);
    }

    ::ng-deep .popover.sound-popover .form-range::-moz-range-thumb {
      background-color: var(--bs-info);
    }
  `],
    standalone: false
})
export class SoundControlComponent {
  /** Render an icon + "Töne" caption to blend into the desktop sidebar. */
  @Input() withLabel = false;

  constructor(public readonly sound: PosSoundService) {}

  get iconName(): string {
    if (this.sound.isMuted() || this.sound.getVolume() === 0) {
      return 'volume_off';
    }
    return this.sound.getVolume() < 0.5 ? 'volume_down' : 'volume_up';
  }

  get volumePercent(): number {
    return Math.round(this.sound.getVolume() * 100);
  }

  /** Live volume update while dragging — no sound to avoid a noisy drag. */
  onVolumeInput(event: Event): void {
    this.sound.setVolume(Number((event.target as HTMLInputElement).value) / 100);
  }

  /** Single preview tick once the slider is released. */
  previewVolume(): void {
    this.sound.click();
  }

  toggleMute(): void {
    const muted = this.sound.toggleMute();
    if (!muted) {
      this.sound.addToCart();
    }
  }
}
